import { ArrowRight, ChevronDown } from "lucide-react";

// Real POS / card payment scenes from Mixkit (verified)
const VIDEO_SRC_PRIMARY = "https://assets.mixkit.co/videos/15914/15914-720.mp4"; // POS card payment
const VIDEO_SRC_FALLBACK = "https://assets.mixkit.co/videos/5407/5407-720.mp4"; // POS checkout
const POSTER_IMG =
  "https://images.unsplash.com/photo-1556742044-3c52d6e88c62?auto=format&fit=crop&w=1920&q=80";

export const Hero = () => {
  return (
    <section
      id="inicio"
      data-testid="hero-section"
      className="relative min-h-screen w-full overflow-hidden bg-[#05162a] text-white isolate"
    >
      {/* Background video */}
      <video
        data-testid="hero-bg-video"
        className="absolute inset-0 w-full h-full object-cover z-0 hero-video"
        autoPlay
        muted
        loop
        playsInline
        preload="auto"
        poster={POSTER_IMG}
      >
        <source src={VIDEO_SRC_PRIMARY} type="video/mp4" />
        <source src={VIDEO_SRC_FALLBACK} type="video/mp4" />
      </video>

      {/* Dark navy gradient overlay for readability */}
      <div
        aria-hidden
        className="absolute inset-0 z-10"
        style={{
          background:
            "linear-gradient(120deg, rgba(5,22,42,0.92) 0%, rgba(10,42,78,0.78) 45%, rgba(5,22,42,0.78) 100%)",
        }}
      />

      {/* Subtle grid overlay */}
      <div
        aria-hidden
        className="absolute inset-0 z-10 opacity-[0.10] pointer-events-none"
        style={{
          backgroundImage:
            "linear-gradient(to right, rgba(255,255,255,0.5) 1px, transparent 1px), linear-gradient(to bottom, rgba(255,255,255,0.5) 1px, transparent 1px)",
          backgroundSize: "64px 64px",
          maskImage:
            "radial-gradient(ellipse 70% 50% at 50% 40%, black 50%, transparent 100%)",
          WebkitMaskImage:
            "radial-gradient(ellipse 70% 50% at 50% 40%, black 50%, transparent 100%)",
        }}
      />

      {/* Teal glow accents */}
      <div
        aria-hidden
        className="absolute -top-32 right-1/4 w-[520px] h-[520px] rounded-full bg-[#1FA08A]/20 blur-[140px] z-10"
      />
      <div
        aria-hidden
        className="absolute bottom-0 left-0 w-[420px] h-[420px] rounded-full bg-[#1FA08A]/15 blur-[120px] z-10"
      />

      {/* Oversized BETEC watermark - editorial outline */}
      <div
        aria-hidden
        className="hidden md:block absolute -right-12 lg:-right-4 top-1/2 -translate-y-1/2 z-10 select-none pointer-events-none"
      >
        <span
          className="font-heading font-extrabold tracking-tighter leading-none text-[18rem] xl:text-[22rem]"
          style={{
            WebkitTextStroke: "1.5px rgba(43,201,174,0.18)",
            color: "transparent",
          }}
        >
          24/7
        </span>
      </div>

      {/* Pixel accent (echoing logo dots) */}
      <div
        aria-hidden
        className="absolute top-36 right-10 md:right-20 z-10 grid grid-cols-3 gap-1.5 opacity-50"
      >
        {[1, 1, 0, 1, 1, 1, 0, 1, 1].map((v, i) => (
          <span
            key={i}
            className={`w-2.5 h-2.5 ${v ? "bg-[#1FA08A]" : "bg-transparent"}`}
          />
        ))}
      </div>

      {/* Content */}
      <div className="relative z-20 max-w-6xl mx-auto px-6 md:px-10 min-h-screen flex flex-col justify-center pt-36 pb-24 md:pt-40 md:pb-32">
        {/* New original element: LIVE telemetry badge */}
        <div
          data-testid="hero-live-badge"
          className="hero-fade-in inline-flex items-center self-start gap-3 mb-10"
          style={{ animationDelay: "0ms" }}
        >
          {/* Vertical marker number */}
          <span className="hidden sm:flex items-center gap-3 text-[10px] uppercase tracking-[0.32em] text-slate-300/70 font-mono">
            <span className="text-[#2bc9ae] font-semibold">/ 01</span>
            <span className="h-px w-10 bg-white/25" />
          </span>

          {/* Live indicator */}
          <span className="inline-flex items-center gap-2.5 px-3 py-1.5 rounded-sm border border-white/15 bg-white/[0.05] backdrop-blur-md">
            <span className="relative inline-flex h-2 w-2">
              <span className="absolute inline-flex h-full w-full rounded-full bg-[#2bc9ae] opacity-75 animate-ping" />
              <span className="relative inline-flex rounded-full h-2 w-2 bg-[#2bc9ae] shadow-[0_0_10px_#2bc9ae]" />
            </span>
            <span className="text-[10.5px] md:text-[11px] uppercase tracking-[0.22em] font-semibold text-white/90 font-mono">
              En operación · Soporte activo
            </span>
          </span>
        </div>

        {/* H1 */}
        <h1
          data-testid="hero-headline"
          className="hero-fade-in font-heading text-white text-[42px] sm:text-5xl md:text-6xl lg:text-[80px] xl:text-[96px] leading-[0.98] tracking-tighter font-bold max-w-5xl"
          style={{ animationDelay: "120ms" }}
        >
          Infraestructura crítica.
          <br />
          <span className="text-[#2bc9ae]">Operación</span>{" "}
          <span className="text-white">continua.</span>
        </h1>

        {/* Subtitle */}
        <p
          data-testid="hero-subtitle"
          className="hero-fade-in mt-8 max-w-2xl text-[15px] md:text-lg leading-relaxed text-slate-200/85 font-light"
          style={{ animationDelay: "240ms" }}
        >
          En <span className="text-white font-semibold">Betec</span> diseñamos e
          implementamos soluciones de{" "}
          <span className="text-white font-medium">
            autoservicio, POS, cartelería digital y turnomáticos
          </span>
          , con soporte técnico 24/7 para empresas que no pueden detenerse.
        </p>

        {/* CTAs */}
        <div
          className="hero-fade-in mt-10 flex flex-col sm:flex-row gap-4"
          style={{ animationDelay: "360ms" }}
        >
          <a
            href="#contacto"
            data-testid="hero-cta-primary"
            className="cta-cotizar group inline-flex items-center justify-center gap-2.5 bg-[#1FA08A] hover:bg-[#2bc9ae] text-white px-8 py-4 rounded-sm font-semibold text-[15px] tracking-wide shadow-[0_10px_40px_-10px_rgba(31,160,138,0.7)] hover:shadow-[0_18px_50px_-10px_rgba(43,201,174,0.8)] hover:-translate-y-0.5 transition-all duration-300"
          >
            <span>Cotizar ahora</span>
            <span aria-hidden className="cta-dot" />
            <ArrowRight className="w-4 h-4 transition-transform duration-300 group-hover:translate-x-1" />
          </a>
          <a
            href="#servicios"
            data-testid="hero-cta-secondary"
            className="inline-flex items-center justify-center gap-2 bg-white/[0.05] backdrop-blur-md border border-white/20 text-white hover:bg-white hover:text-[#0A2A4E] px-8 py-4 rounded-sm font-semibold text-[15px] tracking-wide transition-all duration-300"
          >
            Nuestros servicios
          </a>
        </div>

        {/* Telemetry strip - micro stats - replaces classic trust strip */}
        <div
          data-testid="hero-telemetry-strip"
          className="hero-fade-in mt-16 grid grid-cols-2 md:grid-cols-4 gap-x-8 gap-y-6 max-w-3xl border-t border-white/10 pt-8"
          style={{ animationDelay: "480ms" }}
        >
          <TelemetryItem k="01" label="Mesa de ayuda" value="24 / 7" />
          <TelemetryItem k="02" label="Despliegues" value="Masivos" />
          <TelemetryItem k="03" label="Renovación" value="Tecnológica" />
          <TelemetryItem k="04" label="Obras" value="Civiles · TI" />
        </div>
      </div>

      {/* Scroll indicator */}
      <div
        aria-hidden
        className="absolute bottom-6 left-1/2 -translate-x-1/2 z-20 flex flex-col items-center gap-1.5 text-white/55"
      >
        <span className="text-[10px] uppercase tracking-[0.28em] font-mono">Scroll</span>
        <ChevronDown className="w-4 h-4 animate-bounce" />
      </div>
    </section>
  );
};

const TelemetryItem = ({ k, label, value }) => (
  <div className="flex flex-col gap-1">
    <span className="text-[10px] font-mono tracking-[0.22em] text-[#2bc9ae] font-semibold">
      /{k}
    </span>
    <span className="text-[10.5px] uppercase tracking-[0.18em] text-slate-300/70 font-semibold">
      {label}
    </span>
    <span className="text-base md:text-[17px] font-semibold text-white">
      {value}
    </span>
  </div>
);

export default Hero;
